// src/pages/TeamView/useTeamView.js
import { useState, useEffect } from "react";

import {
  fetchUser,
  fetchManagers,
  fetchTeamOfManager,
  fetchManagerTeam,
  fetchReflection,
  fetchTimesheet,
  fetchSkillsAPI,
  fetchMissingReflection,
  fetchMissingTimesheet
} from "../../api/team_view_api";

const PAGE_SIZE = 10; // cards per page

export default function useTeamView() {
  const email = localStorage.getItem("email");
  const today = new Date().toISOString().split("T")[0];

  const [user, setUser] = useState(null);
  const [managers, setManagers] = useState([]);
  const [subManagers, setSubManagers] = useState([]);
  const [members, setMembers] = useState([]);

  const [selectedManager, setSelectedManager] = useState("");
  const [selectedSubManager, setSelectedSubManager] = useState("");
  const [selectedMember, setSelectedMember] = useState("");

  const [viewType, setViewType] = useState("reflection");

  const [period, setPeriod] = useState("daily");
  const [reflections, setReflections] = useState([]);
  const [loadingRef, setLoadingRef] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);

  // ── Reflection pagination ──
  const [refPage, setRefPage] = useState(1);

  const [tsLoading, setTsLoading] = useState(false);
  const [timesheet, setTimesheet] = useState([]);
  const [tsProject, setTsProject] = useState("");
  const [tsPeriod, setTsPeriod] = useState("daily");
  const [tsValue, setTsValue] = useState(today);

  // ── Timesheet pagination ──
  const [tsPage, setTsPage] = useState(1);

  const [skills, setSkills] = useState([]);
  const [skillsPeriod, setSkillsPeriod] = useState("monthly");
  const [skillsDate, setSkillsDate] = useState("2026-03");
  const [loadingSkills, setLoadingSkills] = useState(false);

  const [openMissing, setOpenMissing] = useState(false);
  const [missingType, setMissingType] = useState("reflection");
  const [missingPeriod, setMissingPeriod] = useState("daily");
  const [missingDate, setMissingDate] = useState(today);
  const [missingUsers, setMissingUsers] = useState([]);
  const [missingLoading, setMissingLoading] = useState(false);
  const [missingSearch, setMissingSearch] = useState("");
  const [totalTeamCount, setTotalTeamCount] = useState(0);
  const [submittedCount, setSubmittedCount] = useState(0);

  const avatar = (n) => (n ? n.charAt(0).toUpperCase() : "U");

  // ── Pagination helpers ──
  const paginate = (arr, page) => {
    const start = (page - 1) * PAGE_SIZE;
    return arr.slice(start, start + PAGE_SIZE);
  };
  const totalPages = (arr) => Math.max(1, Math.ceil(arr.length / PAGE_SIZE));

  const refTotalPages   = totalPages(reflections);
  const tsTotalPages    = totalPages(timesheet);
  const pagedReflections = paginate(reflections, refPage);
  const pagedTimesheet   = paginate(timesheet,   tsPage);

  const formatYMD = (d) => {
    const dt = new Date(d);
    return (
      dt.getFullYear() +
      "-" +
      String(dt.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(dt.getDate()).padStart(2, "0")
    );
  };

  const getWeekRange = (iso) => {
    const [yStr, wStr] = iso.split("-W");
    const year = parseInt(yStr);
    const week = parseInt(wStr);

    const simple = new Date(year, 0, 4);
    const dow = simple.getDay() || 7;
    const monday = new Date(simple);
    monday.setDate(simple.getDate() - (dow - 1));

    const start = new Date(monday);
    start.setDate(start.getDate() + (week - 1) * 7);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);

    return { from: formatYMD(start), to: formatYMD(end) };
  };

  // BLOCK NORMAL EMPLOYEE
  useEffect(() => {
    if (localStorage.getItem("designation")?.toLowerCase() === "employee") {
      alert("You are not allowed to access Team View");
      window.location.href = "/dashboard";
    }
  }, []);

  // LOAD USER + HIERARCHY
  const loadUser = async () => {
    const d = await fetchUser(email);
    if (!d?.employee) return;

    setUser(d.employee);

    if (d.employee.RoleID === 1) {
      const m = await fetchManagers(email);
      setManagers(m.team || []);
    } else if (d.employee.RoleID === 2) {
      const t = await fetchManagerTeam(d.employee.EmailID);
      setMembers(t.team || []);
    } else {
      setMembers([{ Name: d.employee.Name, EmailID: d.employee.EmailID }]);
    }
  };

  const onManagerSelect = async (value) => {
    setSelectedManager(value);
    setSelectedSubManager("");
    setSelectedMember("");
    setReflections([]);
    setTimesheet([]);
    setSkills([]);
    setMembers([]);
    setSubManagers([]);
    setRefPage(1);
    setTsPage(1);

    if (!value) return;

    const d = await fetchTeamOfManager(email, value);

    if (d.level === 2) setSubManagers(d.team || []);
    else setMembers(d.team || []);
  };

  const onSubManagerSelect = async (value) => {
    setSelectedSubManager(value);
    setSelectedMember("");
    setReflections([]);
    setTimesheet([]);
    setSkills([]);
    setMembers([]);
    setRefPage(1);
    setTsPage(1);

    if (!value) return;

    const d = await fetchTeamOfManager(email, value);
    setMembers(d.team || []);
  };

  // ================= REFLECTION =================
  const loadReflections = async (empEmail) => {
    if (!empEmail) return;
    setLoadingRef(true);
    setRefPage(1);

    const d = await fetchReflection(empEmail, period);

    setReflections(d.records || []);
    setExpandedIndex(null);
    setLoadingRef(false);
  };

  // ================= TIMESHEET =================
  // ✅ Auto-load: no filter params — loads ALL timesheets for employee
  const loadTimesheetAuto = async (empEmail) => {
    if (!empEmail) return;
    setTsLoading(true);
    setTsPage(1);

    const params = new URLSearchParams();
    params.append("email", empEmail);
    // No date/project filter — load all

    const d2 = await fetchTimesheet(params);
    setTimesheet(d2.records || []);
    setTsLoading(false);
  };

  // ✅ Filter-load: called only when Apply button is clicked
  const loadTimesheet = async (empEmail) => {
    if (!empEmail) return;
    setTsLoading(true);
    setTsPage(1);

    const params = new URLSearchParams();
    params.append("email", empEmail);

    if (tsProject.trim()) params.append("project", tsProject.trim());
    if (tsPeriod === "daily") params.append("date", tsValue);

    if (tsPeriod === "weekly") {
      const r = getWeekRange(tsValue);
      params.append("fromDate", r.from);
      params.append("toDate", r.to);
    }

    if (tsPeriod === "monthly") params.append("month", tsValue);

    const d2 = await fetchTimesheet(params);
    setTimesheet(d2.records || []);
    setTsLoading(false);
  };

  // ================= SKILLS =================
  const loadSkills = async (empEmail) => {
    if (!empEmail) return;

    setLoadingSkills(true);

    try {
      const d = await fetchSkillsAPI(
        email,
        skillsPeriod,
        skillsDate,
        empEmail
      );

      if (d.status === "empty" || !d.skills) {
        setSkills([]);
        setLoadingSkills(false);
        return;
      }

      const analysis = d.skills.skills;
      const all = analysis.all_skills || {};
      const levels = analysis.skill_levels || {};

      const converted = Object.keys(all).map((name) => ({
        SkillName: name,
        Score: all[name] || 0,
        Level: levels[name] || "-",
        UpdatedDate: d.date_value,
      }));

      setSkills(converted);
    } catch {
      setSkills([]);
    }

    setLoadingSkills(false);
  };

  // ================= MISSING =================
  const fetchMissing = async () => {
    setMissingLoading(true);
    setMissingUsers([]);

    try {
      let d;

      if (missingType === "reflection") {
        d = await fetchMissingReflection(
          email,
          missingPeriod,
          missingDate
        );
      } else {
        d = await fetchMissingTimesheet(email, missingDate);
      }

      setTotalTeamCount(d.total_count || 0);
      setSubmittedCount(d.submitted_count || 0);
      setMissingUsers(d.missing_employees || []);
    } catch {
      setMissingUsers([]);
      setTotalTeamCount(0);
      setSubmittedCount(0);
    }

    setMissingLoading(false);
  };

  useEffect(() => {
    loadUser();
  }, []);

  // ✅ Reflection: auto-load when member/period/viewType changes
  useEffect(() => {
    if (viewType === "reflection" && selectedMember) {
      loadReflections(selectedMember);
    }
  }, [period, selectedMember, viewType]);

  // ✅ Timesheet: auto-load when member selected or viewType switches to timesheet
  useEffect(() => {
    if (viewType === "timesheet" && selectedMember) {
      loadTimesheetAuto(selectedMember);
    }
  }, [selectedMember, viewType]);

  useEffect(() => {
    if (viewType === "skills" && selectedMember) {
      loadSkills(selectedMember);
    }
  }, [skillsPeriod, skillsDate, selectedMember, viewType]);

  return {
    user,
    managers,
    subManagers,
    members,

    selectedManager,
    setSelectedManager,
    selectedSubManager,
    setSelectedSubManager,
    selectedMember,
    setSelectedMember,

    viewType,
    setViewType,

    period,
    setPeriod,

    reflections,
    loadingRef,
    expandedIndex,
    setExpandedIndex,

    // Reflection pagination
    refPage, setRefPage,
    refTotalPages,
    pagedReflections,

    timesheet,
    tsLoading,
    tsProject, setTsProject,
    tsPeriod, setTsPeriod,
    tsValue, setTsValue,

    // Timesheet pagination
    tsPage, setTsPage,
    tsTotalPages,
    pagedTimesheet,

    skills,
    skillsPeriod, setSkillsPeriod,
    skillsDate, setSkillsDate,
    loadingSkills,

    openMissing, setOpenMissing,
    missingType, setMissingType,
    missingPeriod, setMissingPeriod,
    missingDate, setMissingDate,
    missingUsers,
    missingLoading,
    missingSearch, setMissingSearch,
    totalTeamCount,
    submittedCount,

    avatar,
    onManagerSelect,
    onSubManagerSelect,
    loadReflections,
    loadTimesheet,
    loadSkills,
    fetchMissing,
  };
}